import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AllMotorData, BestSellingItem, MotorData } from "../types/types";
import {
  GetAllMealPlansRequest,
  MealPlan,
} from "../../../gen/witbooking/backoffice/mealplan/v1/mealplan_api_pb";
import { useGrpcClient, usePropertyGrpcCall } from "../../../lib/grpc";
import { MealPlanAPIPromiseClient } from "../../../gen/witbooking/backoffice/mealplan/v1/mealplan_api_grpc_web_pb";
import { useLocaleProvider } from "../../../components/locale-provider";
import { getLanguageFromFullLocale, IConfig, useConfig } from "../../../lib/config";
import { AccommodationAPIPromiseClient } from "../../../gen/witbooking/backoffice/accommodation/v1/accommodation_api_grpc_web_pb";
import {
  Accommodation,
  GetAccommodationsRequest,
} from "../../../gen/witbooking/backoffice/accommodation/v1/accommodation_api_pb";
import {useKeycloakAuthz} from "../../../components/keycloak-provider";

export const useGetMyMotor = ({ propertyTicker }: { propertyTicker: string }) => {
  const [data, setData] = useState<AllMotorData | undefined>(undefined);
  const [myMotor, setMyMotor] = useState<MotorData | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [mealPlans, setMealplans] = useState<MealPlan.AsObject[] | undefined>(undefined);
  const [accommodations, setAccomodations] = useState<Accommodation.AsObject[] | undefined>(
    undefined
  );
  const accommodationClient = useGrpcClient(AccommodationAPIPromiseClient);
  const mealPlansClient = useGrpcClient(MealPlanAPIPromiseClient);
  const getAccommodations = usePropertyGrpcCall(
    accommodationClient,
    accommodationClient.getAccommodations
  );

  const locale = useLocaleProvider();
  const configuration: IConfig = useConfig();
  const keycloak = useKeycloakAuthz()

  const currentLocale = useMemo(() => locale && getLanguageFromFullLocale(locale.currentLocale), [
    locale,
  ]);

  const getAccomodationsAndMealPlans = useCallback(async () => {
    const accommodationRequest = new GetAccommodationsRequest();
    const accommodationsPromise = getAccommodations(accommodationRequest);

    const mealPlansRequest = new GetAllMealPlansRequest();
    mealPlansRequest.setPropertyTicker(propertyTicker);
    const mealPlansPromise = mealPlansClient.getAllMealPlans(mealPlansRequest);

    const [accommodations, mealPlans] = await Promise.all([
      accommodationsPromise,
      mealPlansPromise,
    ]);
    setAccomodations(accommodations.getAccommodationList().map(e => e.toObject()));
    setMealplans(mealPlans.getMealPlansList().map(m => m.toObject()));
  }, [getAccommodations, mealPlansClient, propertyTicker]);

  const getBestSellingProductName = useCallback(
    (value: string = "") => {
      if (accommodations?.length) {
        const accommodation = accommodations.find(line => line.ticker === value);
        if (accommodation) {
          const translatedAccommodation = accommodation.translationsList.find(
            t => t.locale === currentLocale
          );
          return translatedAccommodation?.name || value;
        }
      }
      return value;
    },
    [currentLocale, accommodations]
  );

  const getMealPlanName = useCallback(
    (value: string) => {
      if (mealPlans) {
        const mealPlan = mealPlans.find(m => m.ticker === value);
        if (mealPlan) {
          const translatedMealPlan = mealPlan.translationsList.find(
            t => t.locale === currentLocale
          );
          return translatedMealPlan?.name || value;
        }
      }
      return value;
    },
    [currentLocale, mealPlans]
  );

  useEffect(() => {
    if (data) {
      const {
        nextUnavailableDate,
        bestSellingDiscount,
        bestSellingPromotionalCode,
        bestSellingMealPlan,
      } = data;
      const {
        bestSellingProductList,
        bestSellingDiscountList,
        bestSellingPromotionalCodeList,
        bestSellingMealPlanList,
        bestSellingProduct,
      } = data;
      const myMotor = {
        nextUnavailableDate,
        bestSellingDiscount,
        bestSellingProduct: bestSellingProduct
          ? {
              ...bestSellingProduct,
              description: getBestSellingProductName(bestSellingProduct?.description || ""),
              value: getBestSellingProductName(
                bestSellingProduct?.value ? bestSellingProduct.value.toString() : ""
              ),
            }
          : undefined,
        bestSellingPromotionalCode: { ...bestSellingPromotionalCode },
        bestSellingMealPlan: bestSellingMealPlan
          ? {
              ...bestSellingMealPlan,
              description: getMealPlanName(bestSellingMealPlan?.description || ""),
              value: getMealPlanName(
                bestSellingMealPlan?.value ? bestSellingMealPlan.value.toString() : ""
              ),
            }
          : undefined,
      };
      const traslatedBestSellingProductList = bestSellingProductList?.length
        ? bestSellingProductList.map((item: BestSellingItem) => ({
            name: getBestSellingProductName(item?.name),
          }))
        : [];

      const traslatedBestSellingMealPlanList = bestSellingMealPlanList?.length
        ? bestSellingMealPlanList.map((item: BestSellingItem) => ({
            name: getMealPlanName(item?.name),
          }))
        : [];

      setMyMotor({
        myMotor,
        bestSelling: {
          bestSellingProductList: traslatedBestSellingProductList,
          bestSellingDiscountList,
          bestSellingPromotionalCodeList,
          bestSellingMealPlanList: traslatedBestSellingMealPlanList,
        },
      });
      setLoading(false);
    }
  }, [getMealPlanName, getBestSellingProductName, locale, data, loading]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await getAccomodationsAndMealPlans();
        const { data } = await axios.get(`${configuration.myMotorUrl}/${propertyTicker}`, {
          headers: {
            Authorization: keycloak.rpt,
          }
        });
        setData(data);
      } catch (error) {
        setLoading(false);
        console.log("Error in my motor", error);
      }
    };
    fetchData();
  }, [configuration.myMotorUrl, getAccomodationsAndMealPlans, propertyTicker, keycloak]);

  return { data: myMotor, loading: loading };
};
